/**
 * Benefit Data Transformer Utility
 * Transforms benefit code mappings and data array into a formatted benefit structure
 */
import i18next from 'i18next';

const { t } = i18next;

interface CodingType {
  system: string;
  code: string;
  display: string;
  userSelected: boolean;
}

interface DataItem {
  type: {
    coding: CodingType;
  };
  value: string;
}

interface CodeMapping {
  key: string;
  displayTitle: string;
  Primary: string | string[];
  Secondary: string | string[];
}

interface CodeObject {
  generalVisits: CodeMapping[];
  discountedNetwork: CodeMapping[];
}

interface TransformedItem {
  key: string;
  displayTitle?: string;
  value: string;
}

interface TransformedResult {
  generalVisits: TransformedItem[];
  discountedNetwork: TransformedItem[];
}

/**
 * Helper function to find value by code in data array
 */
const findValueByCode = (
  dataArray: DataItem[],
  code: string
): string | null => {
  const item = dataArray.find((dataItem) => dataItem.type.coding.code === code);
  return item ? item.value : null;
};

/**
 * Helper function to check if a value is empty, null, or "N/A"
 */
const isEmptyValue = (value: string | null): boolean => {
  return !value || value.trim() === '' || value.toUpperCase() === 'N/A';
};

/**
 * Helper function to format a single value (copay or coinsurance)
 */
const formatValue = (value: string, type: 'copay' | 'coinsurance'): string => {
  if (type === 'copay') {
    return `$${value} ${t('benefits.copay')}`;
  } else {
    return `${value}% ${t('benefits.coinsurance')}`;
  }
};

/**
 * Process a single code (string or array of strings) and return the first valid value
 */
const processCode = (
  dataArray: DataItem[],
  code: string | string[]
): string | null => {
  if (Array.isArray(code)) {
    // Try each code in the array until we find a valid value
    for (const c of code) {
      const value = findValueByCode(dataArray, c);
      if (!isEmptyValue(value)) {
        return value;
      }
    }
    return null;
  } else {
    return findValueByCode(dataArray, code);
  }
};

/**
 * Transform a single mapping item into a formatted string
 */
const transformMappingItem = (
  mapping: CodeMapping,
  dataArray: DataItem[]
): string => {
  const primaryCode = mapping.Primary;
  const secondaryCode = mapping.Secondary;

  // Process primary code (copay)
  const primaryValue = processCode(dataArray, primaryCode);

  // Process secondary code (coinsurance)
  const secondaryValue = processCode(dataArray, secondaryCode);

  // Determine the output based on the values
  const hasPrimaryValue = !isEmptyValue(primaryValue);
  const hasSecondaryValue = !isEmptyValue(secondaryValue);

  if (hasPrimaryValue) {
    return formatValue(primaryValue!, 'copay');
  } else if (hasSecondaryValue) {
    return formatValue(secondaryValue!, 'coinsurance');
  } else {
    return t('benefits.notAvailable');
  }
};

/**
 * Benefits code mapping object
 */
const benefitsCodeObject: CodeObject = {
  generalVisits: [
    {
      key: 'Primary Care Doctor Visit',
      displayTitle: 'benefits.primaryCareDoctorVisit',
      Primary: 'OV_COPAY',
      Secondary: 'OV_COINS',
    },
    {
      key: 'Specialist Visit',
      displayTitle: 'benefits.specialistVisit',
      Primary: 'SOV_COPAY',
      Secondary: 'SOV_COINS',
    },
    {
      key: 'Urgent Care Center Visit',
      displayTitle: 'benefits.urgentCareCenterVisit',
      Primary: 'UC_COPAY',
      Secondary: 'UC_COINS',
    },
    {
      key: 'Emergency Room Visit',
      displayTitle: 'benefits.emergencyRoomVisit',
      Primary: 'ER_COPAY',
      Secondary: 'ER_COINS',
    },
    {
      key: 'Inpatient',
      displayTitle: 'benefits.inpatient',
      Primary: 'IP_COPAY',
      Secondary: 'IP_COINS',
    },
    {
      key: 'Outpatient',
      displayTitle: 'benefits.outpatient',
      Primary: 'OP_COPAY',
      Secondary: 'OP_COINS',
    },
    {
      key: 'Virtual Visit',
      displayTitle: 'common.virtualVisit',
      Primary: 'VC_COPAY',
      Secondary: 'VC_COINS',
    },
  ],
  discountedNetwork: [
    {
      key: 'Primary Care Doctor Visit',
      displayTitle: 'benefits.primaryCareDoctorVisit',
      Primary: ['OV_HPN_COPAY', 'OV_CIN_COPAY'],
      Secondary: ['OV_CIN_COINS', 'OV_HPN_COINS'],
    },
    {
      key: 'Specialist Visit',
      displayTitle: 'benefits.specialistVisit',
      Primary: ['SOV_HPN_COPAY', 'SOV_CIN_COPAY'],
      Secondary: ['SOV_CIN_COINS', 'SOV_HPN_COINS'],
    },
    {
      key: 'Emergency Room Visit',
      displayTitle: 'benefits.emergencyRoomVisit',
      Primary: 'ER_HPN_COPAY',
      Secondary: 'ER_HPN_COINS',
    },
  ],
};

/**
 * Main transformer function
 * @param dataArray - Array of data items with benefit values
 * @returns Transformed object with formatted benefit values
 */
export const transformBenefitData = (
  dataArray: DataItem[]
): TransformedResult => {
  const result: TransformedResult = {
    generalVisits: [],
    discountedNetwork: [],
  };

  // Transform general visits
  result.generalVisits = benefitsCodeObject.generalVisits.map((mapping) => ({
    key: mapping.key,
    displayTitle: mapping.displayTitle,
    value: transformMappingItem(mapping, dataArray),
  }));

  result.generalVisits.push({
    key: 'Prescription Drugs',
    displayTitle: 'benefits.prescriptionDrugs',
    value: t('yourPlan.generalVisitsRX'),
  });

  // Transform discounted network
  result.discountedNetwork = benefitsCodeObject.discountedNetwork.map(
    (mapping) => ({
      key: mapping.key,
      displayTitle: mapping.displayTitle,
      value: transformMappingItem(mapping, dataArray),
    })
  );

  return result;
};

/**
 * Export types for use in other files
 */
export type {
  CodeObject,
  DataItem,
  TransformedResult,
  TransformedItem,
  CodeMapping,
  CodingType,
};
